// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useParcels } from "../../../context/ParcelContext";
import {
  normalizePhoneDigits,
} from "../../../utils/parcelFormatters";
import { ParcelDetailHeader } from "./ParcelDetailHeader";
import { ParcelEditForm, CityContactForm } from "../../../services/parcelTypes";
import { toForm, toPayload } from "./parcelDetailMapper";
import { ParcelDevelopmentPlansSection } from "./sections/ParcelDevelopmentPlansSection";
import { ParcelOutreachSection } from "./sections/ParcelOutreachSection";
import { ParcelEconomicBenefitSection } from "./sections/ParcelEconomicBenefitSection";
import { Parcel } from "../../../services/parcelTypes";
import { ParcelPropertyDetailsSection } from "./sections/ParcelPropertyDetailsSection";
import { ParcelSiteInfoSection } from "./sections/ParcelSiteInfoSection";
import { ParcelValuationSection } from "./sections/ParcelValuationSection";
import { ParcelMetadataSection } from "./sections/ParcelMetadataSection";

function SkeletonDetail() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-5 w-32 animate-pulse rounded bg-muted" />
      </div>
      <div className="h-48 animate-pulse rounded-xl bg-muted" />
      <div className="h-48 animate-pulse rounded-xl bg-muted" />
    </div>
  );
}


export default function ParcelDetailPage() {
  const params = useParams<{ pin: string }>();
  const pin = params.pin ? decodeURIComponent(params.pin) : "";
  const { byPin, loading, refreshParcel } = useParcels();

  const parcel: Parcel | null = useMemo(() => (pin ? byPin.get(pin) ?? null : null), [byPin, pin]);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<ParcelEditForm | null>(null);

  useEffect(() => {
    if (parcel) setForm(toForm(parcel));
  }, [parcel]);

  useEffect(() => {
    if (!saveMessage) return;

    const timeout = setTimeout(() => {
      setSaveMessage(null);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [saveMessage]);

  const updateForm = <K extends keyof ParcelEditForm>(key: K, value: ParcelEditForm[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateCityContact = (index: number, key: keyof CityContactForm, value: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = [...prev.cityContacts];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, cityContacts: next };
    });
  };

  const addCityContact = () => {
    setForm((prev) =>
      prev
        ? {
          ...prev,
          cityContacts: [...prev.cityContacts, { contactDate: "", staffName: "", staffCategory: "", notes: "" }],
        }
        : prev
    );
  };

  const removeCityContact = (index: number) => {
    setForm((prev) =>
      prev
        ? { ...prev, cityContacts: prev.cityContacts.filter((_, i) => i !== index) }
        : prev
    );
  };

  const handleCancel = () => {
    if (!parcel) return;
    setForm(toForm(parcel));
    setSaveError(null);
    setSaveMessage(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!form || !pin) return;

    const phoneDigits = normalizePhoneDigits(form.ownerContactPhone);
    if (form.ownerContactPhone.trim() && phoneDigits.length !== 10) {
      setSaveError("Owner contact phone must be a valid 10-digit phone number.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveMessage(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/updateparcel/${encodeURIComponent(pin)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toPayload(form)),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to save parcel changes.");
      }

      await refreshParcel(pin);
      setSaveMessage("Changes saved.");
      setIsEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
          <SkeletonDetail />
        </div>
      </div>
    );
  }

  if (!parcel) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <ParcelDetailHeader
        parcel={parcel}
        isEditing={isEditing}
        isSaving={isSaving}
        saveMessage={saveMessage}
        saveError={saveError}
        onEdit={() => {
          setSaveError(null);
          setSaveMessage(null);
          setIsEditing(true);
        }}
        onCancel={handleCancel}
        onSave={handleSave}
      />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="space-y-4">
          {/* Overview Details */}
          <ParcelPropertyDetailsSection parcel={parcel} isEditing={isEditing} />

          {/* Outreach */}
          {/* <ParcelOutreachSection
            parcel={parcel}
            form={form}
            isEditing={isEditing}
            updateForm={updateForm}
            updateCityContact={updateCityContact}
            addCityContact={addCityContact}
            removeCityContact={removeCityContact}
          /> */}

          {/* Development Plans */}
          {/* <ParcelDevelopmentPlansSection
            parcel={parcel}
            form={form}
            isEditing={isEditing}
            updateForm={updateForm}
          /> */}

          {/* Economic Benefit */}
          {/* <ParcelEconomicBenefitSection
            parcel={parcel}
            form={form}
            isEditing={isEditing}
            updateForm={updateForm}
          /> */}

          {/* Site Info */}
          <ParcelSiteInfoSection parcel={parcel} />

          {/* Valuation */}
          <ParcelValuationSection parcel={parcel} />
          
          {/* Metadata & Location */}
          <ParcelMetadataSection parcel={parcel} />
        </div>
      </div>
    </div>
  );
}